import { FIELDS, type FieldDef, type MapKey, type Mapping } from './fields.ts'
import { normalizeHeader } from './normalize.ts'

/** How well a header matches a field: 100 exact, a smaller score for a whole-word match. */
function matchScore(header: string, synonyms: string[], avoid: string[] = []): number {
  if (!header) return 0
  if (synonyms.includes(header)) return 100
  const words = ` ${header} `
  if (avoid.some((w) => words.includes(` ${w} `))) return 0
  let best = 0
  for (const syn of synonyms) {
    if (syn.length >= 2 && words.includes(` ${syn} `)) {
      // Prefer headers that are mostly the synonym ("rate" beats "rate of vat").
      best = Math.max(best, 10 + syn.length - (header.length - syn.length) * 0.5)
    }
  }
  return best
}

function rowScore(row: string[]): number {
  let score = 0
  for (const cell of row) {
    const h = normalizeHeader(cell)
    if (FIELDS.some((f) => matchScore(h, f.synonyms, f.avoid) > 0)) score++
  }
  return score
}

/**
 * Index of the header row: the row among the first 20 with the most cells that
 * look like field names (sheets often start with a title or address block).
 */
export function detectHeaderRow(rows: string[][]): number {
  let best = -1
  let bestScore = 0
  const limit = Math.min(rows.length, 20)
  for (let i = 0; i < limit; i++) {
    const score = rowScore(rows[i] ?? [])
    if (score > bestScore) {
      best = i
      bestScore = score
    }
  }
  if (best >= 0) return best
  const firstFilled = rows.findIndex((r) => r.filter((c) => c.trim() !== '').length >= 2)
  return Math.max(firstFilled, 0)
}

const BARCODE_RE = /^(\d{8}|\d{12,14})$/

/**
 * Suggests a column for each field from the header text, then from the values
 * (a column of 8/12/13/14-digit numbers is a barcode). Each column is used once.
 */
export function autoMap(
  headers: string[],
  sampleRows: string[][],
  extra: readonly FieldDef[] = [],
): Mapping {
  const normalized = headers.map(normalizeHeader)
  const candidates: { field: MapKey; col: number; score: number; order: number }[] = []
  ;[...FIELDS, ...extra].forEach((f, order) => {
    normalized.forEach((h, col) => {
      const score = matchScore(h, f.synonyms, f.avoid)
      if (score > 0) candidates.push({ field: f.key, col, score, order })
    })
  })
  candidates.sort((a, b) => b.score - a.score || a.order - b.order || a.col - b.col)

  const mapping: Mapping = {}
  const used = new Set<number>()
  for (const c of candidates) {
    if (mapping[c.field] !== undefined || used.has(c.col)) continue
    mapping[c.field] = c.col
    used.add(c.col)
  }

  // A "main SKU" column only means variants when the sheet also says which variant.
  if (mapping.group_code !== undefined && mapping.variant === undefined) {
    used.delete(mapping.group_code)
    delete mapping.group_code
  }

  // Many sheets call the product name "Description".
  if (mapping.name === undefined && mapping.description !== undefined) {
    mapping.name = mapping.description
    delete mapping.description
  }

  if (mapping.barcode === undefined) {
    for (let col = 0; col < headers.length; col++) {
      if (used.has(col)) continue
      const values = sampleRows.map((r) => (r[col] ?? '').trim()).filter((v) => v !== '')
      if (
        values.length >= 2 &&
        values.filter((v) => BARCODE_RE.test(v)).length / values.length >= 0.8
      ) {
        mapping.barcode = col
        used.add(col)
        break
      }
    }
  }
  return mapping
}

/** Stable id for a header layout, used to remember a mapping for the next sheet like it. */
export function headerFingerprint(headers: string[]): string {
  const text = headers.map(normalizeHeader).join('|')
  // FNV-1a, 32-bit.
  let hash = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}

/** Drops mapped columns that no longer exist (a remembered mapping on a shorter sheet). */
export function sanitizeMapping(
  mapping: Mapping,
  columnCount: number,
  extra: readonly FieldDef[] = [],
): Mapping {
  const out: Mapping = {}
  const used = new Set<number>()
  for (const f of [...FIELDS, ...extra]) {
    const col = mapping[f.key]
    if (col === undefined || col < 0 || col >= columnCount || used.has(col)) continue
    out[f.key] = col
    used.add(col)
  }
  return out
}
